import React, { useState, useEffect } from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { DocumentTextIcon as DocumentTextIconSolid } from '@heroicons/react/24/solid';
import NotesModal from './NotesModal';
import { getNotesBySiren } from '../../api/notesApi';

interface NotesButtonProps {
  siren?: string;
  siret?: string;
  companyName?: string;
  className?: string;
  showCount?: boolean;
  variant?: 'icon' | 'button' | 'text';
  size?: 'sm' | 'md' | 'lg';
}

const NotesButton: React.FC<NotesButtonProps> = ({
  siren,
  siret,
  companyName,
  className = '',
  showCount = true,
  variant = 'icon',
  size = 'md'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notesCount, setNotesCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  // Extract SIREN from SIRET if needed
  const actualSiren = siren || (siret ? siret.substring(0, 9) : '');

  // Load notes count when component mounts
  useEffect(() => {
    if (actualSiren && showCount) {
      loadNotesCount();
    }
  }, [actualSiren, showCount]);

  const loadNotesCount = async () => {
    if (!actualSiren) return;

    setIsLoading(true);
    try {
      const response = await getNotesBySiren([actualSiren]);
      setNotesCount(response.data[actualSiren]?.length || 0);
    } catch (error) {
      console.error('Error loading notes count:', error);
      setNotesCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Reload count after modal closes to reflect any changes
    if (showCount) {
      loadNotesCount();
    }
  };

  if (!actualSiren) {
    return null;
  }

  const hasNotes = notesCount > 0;
  const IconComponent = hasNotes ? DocumentTextIconSolid : DocumentTextIcon;

  // Size classes
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  const buttonSizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  // Render based on variant
  if (variant === 'icon') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`relative inline-flex items-center justify-center rounded-md text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${className}`}
          title={`Notes ${companyName ? `pour ${companyName}` : ''}`}
          disabled={isLoading}
        >
          <IconComponent 
            className={`${sizeClasses[size]} ${hasNotes ? 'text-blue-600' : ''}`} 
          />
          {showCount && notesCount > 0 && (
            <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-1 py-0.5 text-xs font-medium leading-none text-blue-600 bg-white border border-blue-600 rounded-full min-w-[1rem] h-4">
              {notesCount > 99 ? '99+' : notesCount}
            </span>
          )}
        </button>

        <NotesModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          siren={actualSiren}
          companyName={companyName}
        />
      </>
    );
  }

  if (variant === 'button') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`inline-flex items-center ${buttonSizeClasses[size]} font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${className}`}
          disabled={isLoading}
        >
          <IconComponent 
            className={`${sizeClasses[size]} mr-2 ${hasNotes ? 'text-blue-600' : 'text-gray-400'}`} 
          />
          Notes
          {showCount && notesCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium leading-none text-blue-600 bg-white border border-blue-600 rounded-full">
              {notesCount > 99 ? '99+' : notesCount}
            </span>
          )}
        </button>

        <NotesModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          siren={actualSiren}
          companyName={companyName}
        />
      </>
    );
  }

  if (variant === 'text') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`inline-flex items-center text-blue-600 hover:text-blue-800 focus:outline-none focus:underline transition-colors ${className}`}
          disabled={isLoading}
        >
          <IconComponent className={`${sizeClasses[size]} mr-1`} />
          Notes
          {showCount && notesCount > 0 && (
            <span className="ml-1 text-gray-500">
              ({notesCount})
            </span>
          )}
        </button>

        <NotesModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          siren={actualSiren}
          companyName={companyName}
        />
      </>
    );
  }

  return null;
};

export default NotesButton; 